import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Sun, Menu, X } from "lucide-react";
import { scrollToId } from "@/lib/scroll";

const LINKS = [
  { id: "manifesto", label: "Manifesto" },
  { id: "programs", label: "Programs" },
  { id: "impact", label: "Impact" },
  { id: "journal", label: "Journal" },
  { id: "contact", label: "Contact" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const go = (e, id) => {
    e.preventDefault();
    setOpen(false);
    if (location.pathname === "/") scrollToId(id);
    else navigate(`/#${id}`);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-bone/80 backdrop-blur-md border-b border-forest/15" data-testid="site-navbar">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-[72px] flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
          <img src="/logo-mark.png" alt="Learning Lights Initiative logo" className="h-11 w-auto transition-transform duration-300 group-hover:scale-105" />
          <span className="font-sans font-extrabold text-lg tracking-tight text-forest leading-none uppercase">
            Learning Lights
            <span className="block text-[10px] font-sans font-semibold tracking-[0.34em] uppercase text-moss">Initiative</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8" data-testid="nav-links">
          {LINKS.map((l) => (
            <a
              key={l.id}
              href={`/#${l.id}`}
              onClick={(e) => go(e, l.id)}
              data-testid={`nav-link-${l.id}`}
              className="text-sm font-semibold text-forest/80 hover:text-terra transition-colors duration-200 tracking-wide"
            >
              {l.label}
            </a>
          ))}
          <Link to="/careers" data-testid="nav-link-careers"
            className="text-sm font-semibold text-forest/80 hover:text-terra transition-colors duration-200 tracking-wide">
            Careers
          </Link>
          <a
            href="/#get-involved"
            onClick={(e) => go(e, "get-involved")}
            data-testid="nav-donate-btn"
            className="bg-terra text-bone text-sm font-bold px-6 py-3 hover:-translate-y-0.5 hover:shadow-lg transition-all duration-200"
          >
            Donate
          </a>
        </nav>

        <button
          className="lg:hidden text-forest p-2"
          onClick={() => setOpen(!open)}
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-bone/95 backdrop-blur-md border-b border-forest/15 overflow-hidden"
            data-testid="nav-mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {LINKS.map((l) => (
                <a
                  key={l.id}
                  href={`/#${l.id}`}
                  onClick={(e) => go(e, l.id)}
                  data-testid={`nav-mobile-link-${l.id}`}
                  className="font-serif text-2xl text-forest"
                >
                  {l.label}
                </a>
              ))}
              <Link to="/careers" onClick={() => setOpen(false)} data-testid="nav-mobile-link-careers" className="font-serif text-2xl text-forest">
                Careers
              </Link>
              <a
                href="/#get-involved"
                onClick={(e) => go(e, "get-involved")}
                data-testid="nav-mobile-donate-btn"
                className="bg-terra text-bone font-bold px-6 py-3 text-center mt-2"
              >
                Donate
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
